import type { MarketplaceEntry } from "../types/index";
import type { SupportedClient } from "@contracts/contract-2-plugin-store";
import { CLIENT_CONFIG } from "./ClientBadge";
import { OfficialCheck } from "./OfficialBadge";
import styles from "./PluginCard.module.css";

const SOURCE_LABELS: Record<string, string> = {
  ghec: "GHEC",
  ghes: "GHES",
};

const EXTENSION_TYPE_CLASS: Record<string, string> = {
  plugin: "typeBadgePlugin",
  skill: "typeBadgeSkill",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
interface PluginCardProps {
  plugin: MarketplaceEntry;
  onClick?: () => void;
  onClientClick?: (client: SupportedClient) => void;
  onCategoryClick?: (category: string) => void;
}

export default function PluginCard({ plugin, onClick, onClientClick, onCategoryClick }: PluginCardProps) {
  const cardClass = [styles.card, onClick ? styles.clickable : ""].filter(Boolean).join(" ");
  const updatedDate = plugin.lastUpdatedAt ? formatDate(plugin.lastUpdatedAt) : "";

  return (
    <article className={cardClass}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <OfficialCheck plugin={plugin} />
          <h3 className={styles.title}>
            {onClick ? (
              <button type="button" className={styles.cardButton} onClick={onClick}>
                {plugin.pluginName}
              </button>
            ) : (
              plugin.pluginName
            )}
          </h3>
        </div>
        {plugin.extensionType && (
          <span className={`${styles.typeBadge} ${styles[EXTENSION_TYPE_CLASS[plugin.extensionType] ?? "typeBadgePlugin"]}`}>
            {plugin.extensionType}
          </span>
        )}
      </div>

      <div className={styles.repoInfo}>
        <div>{plugin.orgOrOwner}/{plugin.repoName}</div>
        <div>{SOURCE_LABELS[plugin.source] ?? plugin.source}</div>
      </div>

      <p className={styles.description}>
        {plugin.description || "No description available"}
      </p>

      <div className={styles.metadata}>
        <div>
          <div className={styles.sectionLabel}>Category</div>
          <div className={styles.pillList}>
            {plugin.categories && plugin.categories.length > 0 ? (
              plugin.categories.map((cat: string) => (
                <button
                  key={cat}
                  className={`${styles.pill} ${onCategoryClick ? styles.interactive : ""}`}
                  onClick={onCategoryClick ? () => onCategoryClick(cat) : undefined}
                  type="button"
                >
                  {cat}
                </button>
              ))
            ) : (
              <span className={styles.emptyPill}>Uncategorized</span>
            )}
          </div>
        </div>

        <div>
          <div className={styles.sectionLabel}>Works with</div>
          <div className={styles.pillList}>
            {plugin.supportedClients && plugin.supportedClients.length > 0 ? (
              plugin.supportedClients.map((client) => {
                const cfg = CLIENT_CONFIG[client] ?? { label: client, color: "var(--color-text-secondary)", bg: "var(--color-surface-secondary)" };
                return (
                  <button
                    key={client}
                    className={`${styles.pill} ${onClientClick ? styles.interactive : ""}`}
                    onClick={onClientClick ? () => onClientClick(client) : undefined}
                    type="button"
                  >
                    {cfg.label}
                  </button>
                );
              })
            ) : (
              <span className={styles.emptyPill}>Unknown</span>
            )}
          </div>
        </div>
      </div>

      {updatedDate && (
        <div className={styles.updatedDate}>
          Updated {updatedDate}
        </div>
      )}
    </article>
  );
}
